import { type ReactNode, useEffect, useMemo } from "react";

import {
  formatCurrencyToDollarAmount,
  getCurrencySymbol,
} from "@features/mmm/utils/formatters";
import { type CurrencyCode } from "@north-beam/nb-common";
import { TextInput } from "@shared/text-input";

interface Props {
  currencyCode: CurrencyCode;
  className?: string;
  inputClassName?: string;
  disabled?: boolean;
  readOnly?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  value: number;
  onChange: (value: number) => void;
  minValue?: number;
  onFocus?: () => void;
  onBlur?: () => void;
}

export const CurrencyInput = ({
  currencyCode,
  className,
  inputClassName,
  disabled,
  readOnly,
  leftIcon,
  rightIcon,
  value,
  minValue = 0,
  onChange,
  onFocus,
  onBlur,
}: Props) => {
  const currencySymbol = useMemo(() => {
    return getCurrencySymbol(currencyCode);
  }, [currencyCode]);

  function validateMinNumber(val: number): number {
    return Math.max(minValue, val);
  }
  function parseNumber(val: string) {
    const num = Number(val.replaceAll(",", ""));
    return isNaN(num) ? 0 : num;
  }

  const displayValue = useMemo(() => {
    return formatCurrencyToDollarAmount(value, currencyCode).replaceAll(
      currencySymbol,
      "",
    );
  }, [value, currencyCode, currencySymbol]);

  const fullInputClassName = useMemo(() => {
    const oldInputClassName = inputClassName ? inputClassName : "";
    const isMinValueClassName = value === minValue ? "text-interstellar-1" : "";
    return `${oldInputClassName} ${isMinValueClassName} pl-4 pr-1 text-right`;
  }, [inputClassName, value, minValue]);

  const inputOnBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const val = parseNumber(e.target.value);
    onChange(validateMinNumber(val));
    onBlur?.();
  };

  // Initial render: ensure value is not > minValue
  // Purposefully leaving dependency array without some dependencies
  // NOTE: The more correct thing to do would be ot include `onChange` as
  // dependency and ensure that parent components use onCallback
  useEffect(() => {
    const validatedValue = validateMinNumber(value);
    if (validatedValue !== value) {
      onChange(validatedValue);
    }
  }, [minValue]);

  return (
    <div className="relative">
      <TextInput
        className={className}
        inputClassName={fullInputClassName}
        disabled={disabled}
        isDisabled={disabled}
        readOnly={readOnly}
        leftIcon={leftIcon}
        rightIcon={rightIcon}
        value={displayValue}
        onChange={(e) => onChange(parseNumber(e.target.value))}
        onFocus={onFocus}
        onBlur={inputOnBlur}
      />
      <i className="absolute block translate-x-1 -translate-y-1/2 top-1/2 pointer-events-none w-6 text-start not-italic">
        {currencySymbol}
      </i>
    </div>
  );
};
